import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { resolveBannerLink, type PromoPopup } from '@/domain';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui/common/icon';
import { Modal } from '@/ui/common/modal';
import { usePromoPopup } from './usePromoPopup';

/**
 * Survives SPA remounts (e.g. LiveFeedProvider wrapping AppShell) but resets
 * on a full document reload — matches legacy once-per-bootstrap behaviour.
 */
let promoPopupConsumedThisLoad = false;

/**
 * Bootstrap CMS promo popup (legacy `CheckForPopups`). Shows once per full
 * page load (F5); SPA route changes do not show it again.
 */
export function PromoPopupModal() {
  const popup = usePromoPopup();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!popup || promoPopupConsumedThisLoad) return;
    promoPopupConsumedThisLoad = true;
    setOpen(true);
  }, [popup]);

  if (!popup) return null;

  return (
    <PromoPopupModalView
      popup={popup}
      open={open}
      onClose={() => setOpen(false)}
    />
  );
}

export interface PromoPopupModalViewProps {
  popup: PromoPopup;
  open: boolean;
  onClose: () => void;
}

/** Presentational popup panel — exported for tests. */
export function PromoPopupModalView({ popup, open, onClose }: PromoPopupModalViewProps) {
  const { t } = useTranslation();
  const link = resolveBannerLink(popup.linkUrl);

  // Size to the image’s intrinsic width (capped by viewport) so the close
  // control sits on the image corner, not the full modal panel.
  const image = (
    <img
      src={popup.imageUrl}
      alt=""
      className="block h-auto max-h-[85vh] w-auto max-w-full object-contain"
      draggable={false}
    />
  );

  let body: ReactNode;
  if (link.kind === 'external') {
    body = (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="block">
        {image}
      </a>
    );
  } else if (link.kind === 'internal') {
    body = (
      <Link to={link.href as never} className="block" onClick={onClose}>
        {image}
      </Link>
    );
  } else {
    body = image;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible
      aria-label={t('Promotion', { defaultValue: 'Promotion' })}
      className="z-[100]"
      backdropClassName="bg-transparent"
      panelClassName="bg-transparent shadow-none"
    >
      <div className="relative w-fit max-w-[min(100%,90vw)]">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('Close', { defaultValue: 'Close' })}
          className={cn(
            'absolute top-1 right-1 z-[1010] inline-flex size-8 items-center justify-center',
            'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]',
            'hover:opacity-80 transition-opacity',
          )}
        >
          <Icon name="close" size={16} />
        </button>
        {body}
      </div>
    </Modal>
  );
}
